/**
 * Download a list of urls as individual files. Copied verbatim from
 * `downloadUrlsAsFiles()` in the v3 web-client.
 */
export function downloadUrlsAsFiles(
  /** Array of urls to download from. */
  urls: Array<string>
) {
  const fragment = document.createDocumentFragment();
  const iframes = [];

  urls.forEach((url) => {
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.style.display = 'none';
    iframes.push(iframe);
    fragment.appendChild(iframe);
  });

  document.body.appendChild(fragment);

  window.setTimeout(() => {
    iframes.forEach((iframe) => iframe.remove());
  }, 60 * 1000);
}

type OpenInBrowserFn = (options: {
  variables: { input: { url: string; omitLocaleParam?: boolean } };
}) => void;

/** Opens each download URL in the system browser (used from UXP integration hosts). */
export function openDownloadUrlsInBrowser(
  urls: string[],
  openInBrowser: OpenInBrowserFn
) {
  urls.forEach((url) => {
    openInBrowser({
      variables: { input: { url, omitLocaleParam: true } },
    });
  });
}

/**
 * @param {string} url - The URL of the file to download
 * @param {string} [filename] - The new name of the file after it is downloaded.
 * Specifying an extension is not necessary, as the browser will automatically
 * detect and append the correct file extension based on the file type.
 * If omitted, the original filename (from the url) is used. Note: If the
 * Content-Disposition header specifies a filename, it takes priority over a
 * filename specified in the download attribute or in the url.
 * @returns {void}
 * @description Invokes the download of a file from the specified URL in the browser.
 * This serves as an alternative to:
 * - downloadUrlsAsFiles which doesn't work in iOS Safari for some file types
 * - FileSaver because it will open a new tab when trying to saveAs a url (undesirable UX)
 */
export function downloadExportUrl(url: string, filename = '') {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
}
